import { Home, Building, MapIcon, ArrowRight } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const investmentOptions = [
  {
    id: 1,
    category: "Flat Investment",
    icon: Home,
    options: [
      {
        type: "1 BHK Apartments",
        priceRange: "₹25 - 35 Lakhs",
        area: "450 - 600 sq ft",
        roi: "8-12% annual returns",
        description: "Perfect for young professionals and small families",
        link: "/explore-properties?bhk=1"
      },
      {
        type: "2 BHK Apartments", 
        priceRange: "₹35 - 55 Lakhs",
        area: "700 - 950 sq ft",
        roi: "10-14% annual returns",
        description: "Ideal for growing families and rental income",
        link: "/explore-properties?bhk=2"
      },
      {
        type: "3 BHK Apartments",
        priceRange: "₹55 - 85 Lakhs",
        area: "1000 - 1400 sq ft", 
        roi: "12-16% annual returns",
        description: "Spacious homes for established families",
        link: "/explore-properties?bhk=3"
      }
    ]
  },
  {
    id: 2,
    category: "Bungalow Investment",
    icon: Building,
    options: [
      {
        type: "Independent Bungalows",
        priceRange: "₹1.2 - 2.5 Cr",
        area: "2000 - 4000 sq ft",
        roi: "15-20% annual returns",
        description: "Luxury living with complete privacy and space",
        link: "/explore-properties?propertyType=bungalow"
      },
      // {
      //   type: "Villa Projects",
      //   priceRange: "₹85 Lakhs - 1.5 Cr",
      //   area: "1500 - 2500 sq ft",
      //   roi: "12-18% annual returns", 
      //   description: "Premium villas with community amenities"
      // },
      {
        type: "Rowhouses",
        priceRange: "₹2 - 5 Cr",
        area: "5000+ sq ft",
        roi: "18-25% annual returns",
        description: "Weekend retreats and investment properties",
        link: "/explore-properties?propertyType=rowhouse"

      }
    ]
  },
  {
    id: 3,
    category: "Land Investment",
    icon: MapIcon,
    options: [
      {
        type: "Residential Plots",
        priceRange: "₹8 - 25 Lakhs",
        area: "1000 - 3000 sq ft",
        roi: "20-30% annual returns",
        description: "DTCP approved plots in developing areas",
        link: "explore-properties?propertyType=plot"
      },
      {
        type: "Commercial Shops",
        priceRange: "₹10 - 40 Lakhs",
        area: "2000 - 10000 sq ft",
        roi: "22-32% annual returns",
        description: "Ready infrastructure for manufacturing units",
        link: "explore-properties?propertyType=shop"
      }
    ]
  }
];

const InvestmentVariations = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 bg-muted/30" id="projects">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 animate-fade-up">
          <h2 className="font-display-bold text-4xl md:text-5xl text-primary mb-6">
            Investment Variations
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Choose from our diverse range of investment options designed to match 
            your budget, lifestyle, and financial goals
          </p>
        </div>

        <div className="space-y-16">
          {investmentOptions.map((category, categoryIndex) => {
            const IconComponent = category.icon;
            return (
              <div 
                key={category.id}
                className="animate-fade-up"
                style={{ animationDelay: `${categoryIndex * 0.3}s` }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 bg-luxury rounded-xl flex items-center justify-center">
                    <IconComponent className="h-8 w-8 text-luxury-foreground" />
                  </div>
                  <div>
                    <h3 className="font-display-bold text-3xl text-primary">
                      {category.category}
                    </h3>
                    <p className="text-muted-foreground">
                      Multiple options to suit your investment needs
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.options.map((option, optionIndex) => (
                    <Card 
                      key={optionIndex}
                      className="luxury-card group hover:scale-[1.02] transition-all duration-300"
                      style={{ animationDelay: `${(categoryIndex * 0.3) + (optionIndex * 0.1)}s` }}
                    >
                      <CardContent className="p-6 space-y-4">
                        <div className="space-y-2">
                          <h4 className="font-display text-xl text-primary group-hover:text-luxury transition-colors">
                            {option.type}
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            {option.description}
                          </p>
                        </div>

                        <div className="space-y-3 py-4 border-t border-border">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Price Range</span>
                            <span className="font-medium text-primary">{option.priceRange}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Area</span>
                            <span className="font-medium">{option.area}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Expected ROI</span>
                            <span className="font-medium text-luxury">{option.roi}</span>
                          </div>
                        </div>

                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full text-luxury hover:text-luxury-foreground hover:bg-luxury group"
                          onClick={() => navigate(`${option.link}`)}
                        >
                          View Options
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary/5 via-luxury/5 to-primary/5 rounded-2xl p-8 md:p-12">
            <h3 className="font-display-bold text-2xl md:text-3xl text-primary mb-4">
              Need Help Choosing the Right Investment?
            </h3>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our investment experts will help you analyze your goals and recommend 
              the perfect property investment strategy for maximum returns.
            </p>
              <center>
                <Button
  className="
    btn-luxury 
    px-2 py-1 text-sm
    sm:px-3 sm:py-1 sm:text-base
    md:px-4 md:py-2 md:text-lg
    flex items-center
  "
>
  <span className="truncate">Consult Our Investment Expert</span>
  <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
</Button>
              </center>

          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestmentVariations;